import React, { useState, useRef, useEffect } from "react";
import { FiFilter, FiX } from "react-icons/fi";
import {
  FilterButton,
  FilterDropdown,
  FilterActions,
  FilterButtonStyled,
  FilterLabel,
  FilterContainer,
} from "../styles/FilterStyles";
import { CheckboxContainer, CheckboxLabel } from "../styles/EOSFilterStyles";

const AccuzipList = ({ onFilterChange, fieldName, currentFilter, isDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState(
    currentFilter?.value || []
  );
  const [alignRight, setAlignRight] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    right: 0,
  });
  const [positionReady, setPositionReady] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const scrollableParentsRef = useRef([]);

  const ACCUZIP_OPTIONS = {
    1: "N/A batch",
    2: "Single batch",
    3: "Multiple batch",
  };

  useEffect(() => {
    if (currentFilter?.value) {
      setSelectedStatuses(currentFilter.value);
    } else {
      setSelectedStatuses([]);
    }
  }, [currentFilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      setPositionReady(false);

      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);

      scrollableParentsRef.current = [];
      let parent = containerRef.current?.parentElement;
      while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        const overflow = style.overflow;
        const overflowY = style.overflowY;
        if (
          overflow === "auto" ||
          overflow === "scroll" ||
          overflowY === "auto" ||
          overflowY === "scroll"
        ) {
          scrollableParentsRef.current.push(parent);
          parent.addEventListener("scroll", handleScroll, true);
        }
        parent = parent.parentElement;
      }

      setTimeout(() => {
        if (containerRef.current) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const dropdownWidth = 280;
          const viewportWidth = window.innerWidth;
          const spaceOnRight = viewportWidth - containerRect.right;
          const spaceOnLeft = containerRect.left;

          const top = containerRect.bottom + 4;
          let shouldAlignRight = false;
          let left = 0;
          let right = 0;

          if (spaceOnRight < dropdownWidth && spaceOnLeft > dropdownWidth) {
            shouldAlignRight = true;
            right = viewportWidth - containerRect.right;
          } else {
            left = containerRect.left;
          }

          setAlignRight(shouldAlignRight);
          setDropdownPosition({
            top: typeof top === "number" && !isNaN(top) ? top : 0,
            left: typeof left === "number" && !isNaN(left) ? left : 0,
            right: typeof right === "number" && !isNaN(right) ? right : 0,
          });
          setPositionReady(true);
        } else {
          setDropdownPosition({
            top: 100,
            left: 100,
            right: 0,
          });
          setPositionReady(true);
        }
      }, 0);
    } else {
      setPositionReady(false);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      scrollableParentsRef.current.forEach((parent) => {
        parent.removeEventListener("scroll", handleScroll, true);
      });
      scrollableParentsRef.current = [];
    };
  }, [isOpen]);

  const handleStatusChange = (statusId) => {
    setSelectedStatuses((prev) =>
      prev.includes(statusId)
        ? prev.filter((id) => id !== statusId)
        : [...prev, statusId]
    );
  };

  const handleApply = () => {
    const filter = {
      type: "accuzip",
      value: selectedStatuses,
    };

    onFilterChange(fieldName, filter);
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedStatuses([]);
    onFilterChange(fieldName, null);
    setIsOpen(false);
  };

  const hasActiveFilter =
    currentFilter && currentFilter.value && currentFilter.value.length > 0;

  return (
    <FilterContainer ref={containerRef}>
      <FilterButton
        active={hasActiveFilter}
        onClick={() => setIsOpen(!isOpen)}
        title="Filter by Accuzip List"
      >
        <FiFilter />
        {hasActiveFilter && <FiX size={12} />}
      </FilterButton>

      {isOpen && positionReady && (
        <FilterDropdown
          ref={dropdownRef}
          alignRight={alignRight}
          top={dropdownPosition.top}
          left={dropdownPosition.left}
          right={dropdownPosition.right}
          isDarkMode={isDarkMode}
        >
          <FilterLabel isDarkMode={isDarkMode}>Accuzip List</FilterLabel>

          <CheckboxContainer>
            {Object.entries(ACCUZIP_OPTIONS).map(([id, label]) => (
              <CheckboxLabel key={id} isDarkMode={isDarkMode}>
                <input
                  type="checkbox"
                  checked={selectedStatuses.includes(parseInt(id))}
                  onChange={() => handleStatusChange(parseInt(id))}
                />
                {label}
              </CheckboxLabel>
            ))}
          </CheckboxContainer>

          <FilterActions>
            <FilterButtonStyled className="clear" onClick={handleClear} isDarkMode={isDarkMode}>
              Clear
            </FilterButtonStyled>
            <FilterButtonStyled
              className="apply"
              onClick={handleApply}
              disabled={selectedStatuses.length === 0}
              isDarkMode={isDarkMode}
            >
              Apply
            </FilterButtonStyled>
          </FilterActions>
        </FilterDropdown>
      )}
    </FilterContainer>
  );
};

export default AccuzipList;
